import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { useDeviceStore } from '@namespace/device';
import { useI18n } from '@namespace/i18n';
import { useGetUserSegments } from '@namespace/segments';
import { useUserInfoLoaded } from '@namespace/user';
import { IS_USE_PLEX_API } from '../../constants';
import { gamesStoreActions, useGamesStore } from '../../store';
import { useGetPlatformForFeed } from '../useGetPlatformForFeed';
import { useLoadFavoritesList } from '../useLoadFavoritesList';
import { useOnLangPlatformChange } from '../usePlexCasinoGamesSectionPagination/hooks/useOnLangPlatformChange';
import { useResetDataOnLangPlatformChange } from '../useResetDataOnLangPlatformChange';

const { GET_GAME, GET_PROVIDER, PLEX_GET_GAMES } = gamesStoreActions;

const useLoadCasinoSingleGameDataOld = ({
  gameId,
  providerId,
  casinoType,
  feedName
}) => {
  const games = useGamesStore('games');
  const isGameAlreadyLoaded = !isEmpty(games.data[gameId]);
  const platform = useGetPlatformForFeed();
  const { userSegmentsNames: userSegments } = useGetUserSegments();
  const { userInfoLoaded } = useUserInfoLoaded();
  const { isDesktop } = useDeviceStore();

  const [isGameDataLoading, setIsGameDataLoading] = useState(false);
  const [isGameDataLoaded, setIsGameDataLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (
          !isGameAlreadyLoaded &&
          gameId &&
          !isGameDataLoaded &&
          !isGameDataLoading &&
          userInfoLoaded
        ) {
          setIsGameDataLoading(true);

          const { success } = await GET_GAME({
            gameId,
            platform,
            userSegments,
            isDesktop
          });

          if (success) {
            await GET_PROVIDER(providerId);
          }

          setIsGameDataLoaded(true);
          setIsGameDataLoading(false);
        }
      } catch (e) {
        console.error(e);

        setIsGameDataLoaded(true);
        setIsGameDataLoading(false);
      }
    })();
  }, [
    gameId,
    providerId,
    isGameDataLoaded,
    isGameDataLoading,
    isGameAlreadyLoaded,
    platform,
    userSegments,
    userInfoLoaded
  ]);

  useLoadFavoritesList(casinoType, feedName);

  return {
    isSingleGameDataLoaded: isGameDataLoaded || isGameAlreadyLoaded,
    isGameDataLoading,
    platform
  };
};

const useLoadCasinoSingleGameDataPlex = ({
  gameId,
  providerId,
  casinoType,
  feedName
}) => {
  const { lobbies, games } = useGamesStore(['lobbies', 'games']);

  const { isProvidersLoaded = false } = lobbies[casinoType] || {};

  const isGameAlreadyLoaded = !isEmpty(games.data[gameId]);
  const platform = useGetPlatformForFeed();
  const { userSegmentsNames: userSegments } = useGetUserSegments();
  const [isGameDataLoading, setIsGameDataLoading] = useState(false);
  const [isGameDataLoaded, setIsGameDataLoaded] = useState(false);
  const { language: lang } = useI18n();
  useOnLangPlatformChange(() => setIsGameDataLoading(false));
  useOnLangPlatformChange(() => setIsGameDataLoaded(false));

  useEffect(() => {
    (async () => {
      try {
        // need to start waiting also for userInfoLoaded when actually start using userSegments with plex
        if (
          !isGameAlreadyLoaded &&
          gameId &&
          !isGameDataLoaded &&
          !isGameDataLoading &&
          feedName
          // && userInfoLoaded
        ) {
          setIsGameDataLoading(true);

          await PLEX_GET_GAMES({
            filter: { id: gameId },
            platform,
            userSegments,
            lang,
            isSetProviderData: true
          });

          setIsGameDataLoaded(true);
          setIsGameDataLoading(false);
        }
      } catch (e) {
        console.error(e);

        setIsGameDataLoaded(true);
        setIsGameDataLoading(false);
      }
    })();
  }, [
    gameId,
    providerId,
    isGameDataLoaded,
    isGameDataLoading,
    isGameAlreadyLoaded,
    platform,
    userSegments,
    feedName,
    lang
  ]);

  useLoadFavoritesList(casinoType, feedName);

  useResetDataOnLangPlatformChange();

  return {
    // game-data is loaded if we have a response from single game get request
    // or if game was loaded previously (on lobby for example) and providers-data were loaded also
    isSingleGameDataLoaded:
      isGameDataLoaded || (isGameAlreadyLoaded && isProvidersLoaded),
    isGameDataLoading,
    platform
  };
};

export const useLoadCasinoSingleGameData = IS_USE_PLEX_API
  ? useLoadCasinoSingleGameDataPlex
  : useLoadCasinoSingleGameDataOld;
